import { useEffect, useRef, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useFragment, graphql } from 'react-relay';
import { useSelector } from 'react-redux';
import classnames from 'classnames';

import { trackEvent } from 'dibs-tracking';
import { ItemTileCarousel } from 'dibs-search-product-tile/exports/ItemTile';
import { useSubscriptionSkippable } from 'dibs-subscriptions-utils/exports/useSubscriptionSkippable';
import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider';
import FavoritesItemProvider from 'dibs-portfolio-folder/exports/FavoritesItemProvider';
import { isAbTestVariant } from 'dibs-buyer-layout/exports/clientABTestHelpers';

import { FavoritesItem } from '../../components/global/FavoriteIcons/FavoritesItem';
import { tileMobileGridTypes, SB_MOBILE_ITEM_TILE_DATA_TAG } from '../../constants/sbConstants';
import { SRC_SET_SIZES_TILE } from '../../constants/imageConstants';

import styles from './SharedItemTile.scss';

const itemSearchFragment = graphql`
    fragment SharedItemTile_itemSearch on ItemSearchQueryConnection {
        ...ItemTileCarousel_itemSearch
        pageType
    }
`;
const itemFragment = graphql`
    fragment SharedItemTile_item on Item
    @argumentDefinitions(
        photosLimit: { type: "Int", defaultValue: 1 }
        fetchVideo: { type: "Boolean", defaultValue: false }
        pageDisplayEnum: { type: "PageDisplayEnum", defaultValue: searchAndBrowse }
        userCountryCode: { type: "String", defaultValue: "" }
        priceBookName: { type: "String" }
    ) {
        ...ItemTileCarousel_item
            @arguments(
                showSeller: $showSeller
                isTrade: $isTrade
                fetchVideo: $fetchVideo
                pageDisplayEnum: $pageDisplayEnum
                userCountryCode: $userCountryCode
                priceBookName: $priceBookName
            )
        ...FavoritesItem_item
        ...FavoritesItemProvider_item
        isAuctionEnabled
        serviceId
        productPhotos: photos(limit: $photosLimit) {
            smallPath
            versions {
                webPath
            }
        }
    }
`;

const userFragment = graphql`
    fragment SharedItemTile_user on User {
        ...FavoritesItemProvider_user
    }
`;

const viewerFragment = graphql`
    fragment SharedItemTile_viewer on Viewer {
        # isSbMobileGridToggleDisabled: isEnabled(key: "isSbMobileGridToggleDisabled")
        isAuctionsSBSubscriptionsFF: featureFlag(feature: "auctionsSBSubscription")
        ...ItemTileCarousel_viewer
            @arguments(
                fetchRegionalInfo: $fetchRegionalInfo
                userZipCode: $userZipCode
                userCountryCode: $userCountryCode
            )
    }
`;

export function SharedItemTile(props) {
    const {
        favorites,
        index,
        item: staticItemRef,
        user: userRef,
        isClient,
        isVisuallySimilarEnabled,
        isMobile,
        lazyLoadStartIndex,
        onClick,
        onContentLoaded,
        showInternalSellerName,
        showSellerName,
        showCreatorName,
        showMeasurements,
        showStorefrontOnlyBadge,
        measurementUnit,
        quickView,
        imageLoadVerticalOffset,
        onContentVisible,
        customClassName,
        imageSize,
        viewer: viewerRef,
        itemSearch: itemSearchRef,
        gridType,
        tileType,
        lastViewedItemIndex,
        showSwipeIndicator,
        fetchFolderOnMouseOver,
        showFavoritesTooltip,
        displayOriginalListPrice,
    } = props;
    const [itemRef, setItemRef] = useState(staticItemRef);
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);
    const viewer = useFragment(viewerFragment, viewerRef);
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const tileElementRef = useRef(null);
    const newTabVariant = isClient && isAbTestVariant(document.cookie, 'NEW_TAB_TEST');
    const { activeQuickView, closeQuickView, openQuickView } = quickView || {};
    const isSbMobileGridToggleDisabled = viewer?.isSbMobileGridToggleDisabled || false;
    const isAuctionsSBSubscriptionsFF = viewer?.isAuctionsSBSubscriptionsFF;
    const itemId = item?.serviceId || '';
    const isAuctionsSbPage = itemSearch?.pageType === 'auction';
    const variables = useSelector(state => state.relayVariables.variables);
    const { showSeller, isTrade, userCountryCode, priceBookName } = variables || {};

    const shouldSkipSubscription = !(
        isAuctionsSBSubscriptionsFF &&
        isAuctionsSbPage &&
        item?.isAuctionEnabled
    );
    const subscriptionConfig = useMemo(
        () => ({
            subscription: graphql`
                subscription SharedItemTileSubscription(
                    $input: AuctionItemUpdateSubscriptionInput!
                    $isTrade: Boolean
                    $showSeller: Boolean = false # $pageDisplayEnum: PageDisplayEnum
                    $userCountryCode: String = ""
                    $priceBookName: String
                ) {
                    auctionItemUpdate(input: $input) {
                        item {
                            ...SharedItemTile_item
                                @arguments(
                                    userCountryCode: $userCountryCode
                                    priceBookName: $priceBookName
                                )
                        }
                    }
                }
            `,
            variables: {
                input: {
                    itemId,
                },
                showSeller,
                isTrade,
                pageDisplayEnum: 'auctionsSearchAndBrowse',
                userCountryCode,
                priceBookName,
            },
            onNext: response => {
                const ref = response?.auctionItemUpdate?.item;
                if (ref) {
                    setItemRef(ref);
                }
            },
        }),
        [itemId, showSeller, isTrade, userCountryCode, priceBookName]
    );

    useSubscriptionSkippable(subscriptionConfig, shouldSkipSubscription);

    function onQuickViewDetailsClick() {
        trackEvent({
            category: 'ecommerce',
            label: itemId,
            action: 'product detail view - from seo quicklook',
        });
    }

    useEffect(() => {
        if (
            !isSbMobileGridToggleDisabled &&
            window.scrollY &&
            index === lastViewedItemIndex &&
            tileElementRef &&
            tileElementRef.current
        ) {
            tileElementRef.current.scrollIntoView({ block: 'center' });
        }
    }, [gridType, index, tileElementRef, lastViewedItemIndex, isSbMobileGridToggleDisabled]);

    return (
        <CurrencyProvider
            render={({ currency, showCurrency }) => {
                const sharedProps = {
                    item,
                    index,
                    imageLoadVerticalOffset,
                    onClick,
                    onContentLoaded,
                    imageSize,
                };
                const photo = item?.productPhotos?.[0];
                if (!photo?.smallPath && !photo?.versions?.[0]?.webPath) {
                    return null;
                }
                return (
                    <div
                        className={classnames(styles.tileWrapper, {
                            [styles.tileWidth]: !customClassName,
                            [customClassName]: !!customClassName,
                            [styles.completeInfo]: true,
                        })}
                        data-tn={SB_MOBILE_ITEM_TILE_DATA_TAG}
                        data-tile-index={index}
                        ref={tileElementRef}
                    >
                        <FavoritesItemProvider
                            item={item}
                            user={user}
                            applyMouseEvents={fetchFolderOnMouseOver}
                            className={styles.favoritesItemProvider}
                        >
                            {({ isFolderVisible, ...favoritesItemProps }) => {
                                const applyIconWrapperClass = isMobile || !fetchFolderOnMouseOver;
                                const favoritesComponent = favorites ? (
                                    <FavoritesItem
                                        wrapperClass={styles.favoritesWrapper}
                                        heartIconWrapperClass={classnames(
                                            styles.heartIconWrapperSpacing,
                                            {
                                                [styles.reducedIcon]: applyIconWrapperClass,
                                            }
                                        )}
                                        folderIconWrapperClass={
                                            applyIconWrapperClass ? styles.reducedIcon : undefined
                                        }
                                        item={item}
                                        {...favorites}
                                        fetchFolder={
                                            favorites.fetchFolder &&
                                            (!fetchFolderOnMouseOver || isFolderVisible)
                                        }
                                        showTooltip={showFavoritesTooltip}
                                        {...favoritesItemProps}
                                    />
                                ) : null;

                                return (
                                    <ItemTileCarousel
                                        {...sharedProps}
                                        useLoFiLazyLoader
                                        itemsPerRow={lazyLoadStartIndex}
                                        activeQuickView={activeQuickView}
                                        isMobile={isMobile}
                                        onContentVisible={onContentVisible}
                                        openQuickView={openQuickView}
                                        closeQuickView={closeQuickView}
                                        srcSetSizes={SRC_SET_SIZES_TILE}
                                        showQuickViewIcon={!!isClient}
                                        showSellerName={showSellerName}
                                        showCreatorName={showCreatorName}
                                        showInternalSellerName={showInternalSellerName}
                                        showStorefrontOnlyBadge={showStorefrontOnlyBadge}
                                        currency={currency}
                                        showPrice={isClient && showCurrency}
                                        measurementUnit={measurementUnit}
                                        onQuickViewDetailsClick={onQuickViewDetailsClick}
                                        favoritesComponent={favoritesComponent}
                                        showMeasurements={showMeasurements}
                                        showBrowseSimilar={isVisuallySimilarEnabled && isClient}
                                        viewer={viewer}
                                        tileType={tileType}
                                        itemSearch={itemSearch}
                                        showSwipeIndicator={showSwipeIndicator}
                                        openInNewTab={newTabVariant}
                                        displayOriginalListPrice={displayOriginalListPrice}
                                    />
                                );
                            }}
                        </FavoritesItemProvider>
                    </div>
                );
            }}
        />
    );
}

SharedItemTile.defaultProps = {
    isMobile: true,
    lazyLoadStartIndex: 2,
    size: 'medium',
    shippingBadge: null,
    gridType: tileMobileGridTypes.COMPLETE_PRODUCT_INFO,
    lastViewedItemIndex: 0,
    displayOriginalListPrice: false,
};

SharedItemTile.propTypes = {
    favorites: PropTypes.object,
    quickView: PropTypes.shape({
        activeQuickView: PropTypes.number,
        closeQuickView: PropTypes.func,
        openQuickView: PropTypes.func,
    }),
    index: PropTypes.number.isRequired,
    isMobile: PropTypes.bool,
    isVisuallySimilarEnabled: PropTypes.bool,
    item: PropTypes.object.isRequired,
    user: PropTypes.object,
    lazyLoadStartIndex: PropTypes.number,
    imageLoadVerticalOffset: PropTypes.number,
    onClick: PropTypes.func,
    onContentLoaded: PropTypes.func,
    showInternalSellerName: PropTypes.bool,
    showSellerName: PropTypes.bool,
    showCreatorName: PropTypes.bool,
    showMeasurements: PropTypes.bool,
    showStorefrontOnlyBadge: PropTypes.bool,
    measurementUnit: PropTypes.string,
    isClient: PropTypes.bool.isRequired,
    onContentVisible: PropTypes.func,
    customClassName: PropTypes.string,
    imageSize: PropTypes.string,
    hideDivider: PropTypes.bool,
    viewer: PropTypes.object,
    itemSearch: PropTypes.object,
    gridType: PropTypes.oneOf([
        tileMobileGridTypes.COMPLETE_PRODUCT_INFO,
        tileMobileGridTypes.PRODUCT_PHOTO_ONLY,
    ]),
    tileType: PropTypes.string,
    lastViewedItemIndex: PropTypes.number,
    showSwipeIndicator: PropTypes.bool,
    fetchFolderOnMouseOver: PropTypes.bool,
    showFavoritesTooltip: PropTypes.bool,
    displayOriginalListPrice: PropTypes.bool,
};
